import React, { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { LANGUAGES } from '../constants';

const AdditionalSectionsEditor = ({ data, onUpdate, parameterName, activeLang, onLangChange }) => {
    // Legacy mapping: `additionalSections` is an ARRAY of objects.
    // Each object has fields like `heading`, `description`, `list`, `type` etc.
    const [activeIndex, setActiveIndex] = useState(0);
    const [subSection, setSubSection] = useState('heading');
    // activeLang via props

    // Create New Section Template
    const addSection = () => {
        const newData = [...(data || [])];
        newData.push({
            type: "full",
            valueDependent: "",
            valueType: "",
            heading: { en: "", hi: "", ar: "" },
            description: { en: "", hi: "", ar: "" },
            descriptionImage: "",
            showHeadingIcon: false,
            showListIcons: true,
            listType: "",
            list: [{ text: { en: "", hi: "", ar: "" }, image: "" }]
        });
        onUpdate(newData);
        setActiveIndex(newData.length - 1);
    };

    // Delete Current Section
    const deleteSection = () => {
        if (!data || data.length === 0) return;

        const newData = data.filter((_, idx) => idx !== activeIndex);
        onUpdate(newData);

        // Adjust active index
        if (newData.length === 0) {
            setActiveIndex(0); // Will show empty state
        } else if (activeIndex >= newData.length) {
            setActiveIndex(newData.length - 1);
        }
    };

    if (!data || data.length === 0) {
        return (
            <div className="pcr-empty-state">
                <p>No additional sections configured.</p>
                <button className="pcr-btn-primary" onClick={addSection}>
                    <FaPlus /> Add New Section
                </button>
            </div>
        );
    }

    const currentSection = data[activeIndex];

    // Helper to update specific field in current section (with deep clone to avoid mutation)
    const updateField = (field, val, lang = null) => {
        // Deep clone the item at activeIndex to avoid mutating original data
        const item = { ...data[activeIndex] };

        if (lang) {
            // Deep clone the nested field object
            const fieldObj = { ...(item[field] || {}) };
            fieldObj[lang] = val;
            item[field] = fieldObj;
        } else {
            item[field] = val;
        }

        // Create new array with cloned item at activeIndex
        const newData = data.map((d, idx) => idx === activeIndex ? item : d);
        onUpdate(newData);
    };

    // List Management
    const addListItem = () => {
        const newData = [...data];
        // Deep clone active item and list to avoid mutation
        const activeItem = { ...newData[activeIndex] };
        activeItem.list = [...(activeItem.list || [])];

        activeItem.list.push({
            text: { en: "", hi: "", ar: "" },
            image: ""
        });

        newData[activeIndex] = activeItem;
        onUpdate(newData);
    };

    const removeListItem = (idx) => {
        // Deep clone for immutable update
        const newData = [...data];
        if (!newData[activeIndex]) return;

        const activeItem = { ...newData[activeIndex] };

        // Ensure list exists before filtering
        if (!activeItem.list) {
            activeItem.list = [];
        } else {
            // Clone list and specific item logic not strictly needed if we just filter
            // But we need to check the text emptiness logic first as per original code

            // 1. Access the specific item safely
            const itemToCheck = activeItem.list[idx];
            if (itemToCheck) {
                // Check if text is effectively empty for activeLang clearing logic
                // The original code mutated text[activeLang] = "" directly on the array?
                // Original: newData[activeIndex].list[idx].text[activeLang] = ""; 

                // If the intent is to clear text first, we should clone:
                activeItem.list = [...activeItem.list];
                activeItem.list[idx] = { ...activeItem.list[idx], text: { ...activeItem.list[idx].text } };
                if (activeItem.list[idx].text) {
                    activeItem.list[idx].text[activeLang] = "";
                }

                // 2. Check all empty
                const currentItem = activeItem.list[idx];
                const allEmpty = LANGUAGES.every(l => {
                    const val = currentItem.text?.[l.code];
                    return !val || val.trim() === "";
                });

                // 3. Filter if empty
                if (allEmpty) {
                    activeItem.list = activeItem.list.filter((_, i) => i !== idx);
                }
            }
        }

        newData[activeIndex] = activeItem;
        onUpdate(newData);
    };

    const updateListItem = (idx, field, val, lang = null) => {
        // Deep clone to avoid mutation
        const newData = data.map((d, i) => {
            if (i !== activeIndex) return d;
            return {
                ...d,
                list: d.list ? [...d.list] : []
            };
        });

        // Ensure path exists
        if (!newData[activeIndex]) return;
        if (!newData[activeIndex].list) newData[activeIndex].list = [];
        if (!newData[activeIndex].list[idx]) return;

        // Clone the specific list item
        newData[activeIndex].list[idx] = { ...newData[activeIndex].list[idx] };

        if (lang) {
            // Ensure the field object exists
            if (!newData[activeIndex].list[idx][field] || typeof newData[activeIndex].list[idx][field] !== 'object') {
                newData[activeIndex].list[idx][field] = {};
            }
            newData[activeIndex].list[idx][field] = { ...newData[activeIndex].list[idx][field] };
            newData[activeIndex].list[idx][field][lang] = val;
        } else {
            newData[activeIndex].list[idx][field] = val;
        }
        onUpdate(newData);
    };

    return (
        <div>
            <h2 className="pcr-section-title">Additional Sections</h2>

            {/* 1. Section Selector */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {data.map((_, idx) => (
                        <button
                            key={idx}
                            className={`pcr-tab-btn ${activeIndex === idx ? 'active' : ''}`}
                            onClick={() => setActiveIndex(idx)}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            Section {idx + 1}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem', borderLeft: '1px solid #e2e8f0', paddingLeft: '0.5rem' }}>
                    <button
                        className="pcr-btn-secondary"
                        onClick={addSection}
                        style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                        title="Add New Section"
                    >
                        <FaPlus />
                    </button>
                    <button
                        className="pcr-btn-danger-icon"
                        onClick={deleteSection}
                        style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', height: 'auto', width: 'auto' }}
                        title="Delete Current Section"
                    >
                        <FaTrash />
                    </button>
                </div>
            </div>

            {/* 2. Sub-Field Navigation */}
            <div className="pcr-sub-editor">
                <div style={{ marginBottom: '1rem' }}>
                    <label className="pcr-editor-label">Edit Property</label>
                    <select
                        className="pcr-nav-select w-100"
                        value={subSection}
                        onChange={(e) => setSubSection(e.target.value)}
                    >
                        <option value="heading">Heading</option>
                        <option value="description">Description</option>
                        <option value="list">List Content</option>
                        <option value="type">Layout Type</option>
                        <option value="valueDependent">Value Dependent?</option>
                    </select>
                </div>

                {/* 3. Dynamic Editor */}
                {['heading', 'description'].includes(subSection) && (
                    <div className="pcr-lang-section">
                        <div className="pcr-lang-tabs">
                            {LANGUAGES.map(lang => (
                                <div
                                    key={lang.code}
                                    className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                                    onClick={() => onLangChange(lang.code)}
                                >
                                    {lang.label}
                                </div>
                            ))}
                        </div>
                        <textarea
                            className="pcr-textarea"
                            value={currentSection[subSection]?.[activeLang] || ''}
                            onChange={(e) => updateField(subSection, e.target.value, activeLang)}
                            placeholder={`Enter ${subSection}...`}
                        />
                    </div>
                )}

                {subSection === 'list' && (
                    <div>
                        <div className="pcr-lang-tabs">
                            {LANGUAGES.map(lang => (
                                <div
                                    key={lang.code}
                                    className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                                    onClick={() => onLangChange(lang.code)}
                                >
                                    {lang.label}
                                </div>
                            ))}
                        </div>
                        {(currentSection.list || []).map((item, idx) => (
                            <div key={idx} className="pcr-list-item-card">
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <textarea
                                            className="pcr-textarea-small"
                                            value={item.text?.[activeLang] || ''}
                                            onChange={(e) => updateListItem(idx, 'text', e.target.value, activeLang)}
                                            placeholder="Item text..."
                                        />
                                    </div>
                                    <div style={{ width: '150px' }}>
                                        <input
                                            type="text"
                                            className="pcr-input-small"
                                            value={item.image || ''}
                                            onChange={(e) => updateListItem(idx, 'image', e.target.value)}
                                            placeholder="Icon..."
                                        />
                                    </div>
                                    <button
                                        className="pcr-btn-danger-icon"
                                        onClick={() => removeListItem(idx)}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button className="pcr-btn-secondary" onClick={addListItem}>
                            <FaPlus /> Add List Item
                        </button>
                    </div>
                )}

                {subSection === 'type' && (
                    <div className="pcr-generic-input">
                        <label className="pcr-editor-label">Layout Type</label>
                        <select
                            className="pcr-input"
                            value={currentSection.type}
                            onChange={(e) => updateField('type', e.target.value)}
                        >
                            <option value="full">Full Width</option>
                            <option value="half">Half Width</option>
                            <option value="third">Third Width</option>
                        </select>
                    </div>
                )}

                {subSection === 'valueDependent' && (
                    <div className="pcr-generic-input">
                        <label className="pcr-editor-label">Value Dependent Configuration</label>
                        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                            Does this section only appear for specific test result values?
                        </p>
                        <select
                            className="pcr-input"
                            value={currentSection.valueDependent || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                const item = { ...data[activeIndex] };
                                item.valueDependent = val;
                                
                                // clear valueType if they turn it off
                                if (val !== 'Yes') {
                                    item.valueType = '';
                                }
                                
                                const newData = data.map((d, idx) => idx === activeIndex ? item : d);
                                onUpdate(newData);
                            }}
                            style={{ marginBottom: '1rem' }}
                        >
                            <option value="">No (Always Show)</option>
                            <option value="Yes">Yes (Value Dependent)</option>
                        </select>

                        {currentSection.valueDependent === 'Yes' && (
                            <div style={{ marginTop: '1rem' }}>
                                <label className="pcr-editor-label">Value Type (Condition)</label>
                                <select
                                    className="pcr-input"
                                    value={currentSection.valueType || ''}
                                    onChange={(e) => updateField('valueType', e.target.value)}
                                >
                                    <option value="">Select condition...</option>
                                    <option value="low">Low</option>
                                    <option value="high">High</option>
                                    <option value="borderline">Borderline</option>
                                    <option value="critical low">Critical Low</option>
                                    <option value="very high">Very High</option>
                                </select>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdditionalSectionsEditor;
